import React, { useState } from "react";
import { remindersAndEvents } from "../../mocks/calendarMocks";
import { type MyLatestReminderList } from "../types";
import { Calendar } from "@/components/ui/calendar";
import "react-day-picker/dist/style.css";

interface CalendarContainerProps {
  onSelectDate: (date: Date | null, events: MyLatestReminderList["reminderList"]) => void;
}

const CalendarContainer: React.FC<CalendarContainerProps> = ({ onSelectDate }) => {
  const startDate = new Date();
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const handleDayClick = (date: Date | undefined) => {
    if (!date) return;
    const dateString = date.toISOString().slice(0, 10);
    const eventsForDay = remindersAndEvents
      .filter(event => event.datetime.slice(0, 10) === dateString)
      .map(event => ({
        id: event.id,
        title: event.title,
        datetime: event.datetime,
        matterId: event.matterId,
        location: event.location,
        party: event.party,
        type: event.type,
        summary: event.summary,
        documentId: event.documentId,
        calendarReminder: event.calendarReminder,
        smsReminder: event.smsReminder,
        assignedUserIds: event.assignedUserIds,
      }));

    if (selectedDate && dateString === selectedDate.toISOString().slice(0, 10)) {
      setSelectedDate(null);
      onSelectDate(null, []);
    } else {
      setSelectedDate(date);
      onSelectDate(date, eventsForDay);
    }
  };

  const monthOffset = new Array(6)
    .fill(null)
    .map((_, index) => new Date(startDate.getFullYear(), startDate.getMonth() + index, 1));

  const countEventsOnDate = (date: Date) => {
    const dateStr = date.toISOString().slice(0, 10);
    return remindersAndEvents.filter(event => event.datetime.slice(0, 10) === dateStr).length;
  };

  return (
    <div className="grid grid-cols-3 gap-4 p-4">
      {monthOffset.map((monthStart, index) => (
        <Calendar
          key={index}
          month={monthStart}
          onSelect={handleDayClick}
          mode="single"
          selected={selectedDate || undefined}
          modifiers={{
            hasEvents: day =>
              day &&
              remindersAndEvents.some(
                event => new Date(event.datetime).toISOString().slice(0, 10) === day.toISOString().slice(0, 10),
              ),
            multipleEvents: day => (day ? countEventsOnDate(day) > 1 : false),
            today: day => (day ? day.toISOString().slice(0, 10) === new Date().toISOString().slice(0, 10) : false),
            selected: day =>
              selectedDate && day ? day.toISOString().slice(0, 10) === selectedDate.toISOString().slice(0, 10) : false,
          }}
          modifiersClassNames={{
            hasEvents:
              'after:content-["•"] after:block after:absolute after:bottom-0 after:translate-y-2 after:left-1/2 after:-translate-x-1/2 after:text-green-500 after:text-md',
            multipleEvents:
              'after:content-["••"] after:block after:absolute after:bottom-0 after:translate-y-2 after:left-1/2 after:-translate-x-1/2 after:text-green-500 after:text-md',
            selected: "bg-blue-500 text-white",
            today: "bg-blue-200",
          }}
        />
      ))}
    </div>
  );
};

export default CalendarContainer;
